import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  Grid,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import apiClient from '../services/api/apiClient';

export default function ProfileSettingsPage() {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone_number || '',
  });

  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  // Initialise with the already-saved photo URL so it shows immediately on page load
  const [avatarPreview, setAvatarPreview] = useState<string>(user?.avatar_url || '');

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChangePassword = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword) {
      setError('All password fields are required');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    try {
      setPasswordLoading(true);
      setError(null);

      await apiClient.post('/admin/users/change-password/', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
        confirm_password: passwordForm.confirmPassword,
      });

      setSuccess(true);
      setShowPasswordForm(false);
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to change password');
      console.error('Error changing password:', err);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.firstName || !formData.email) {
      setError('First name and email are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Create FormData to handle file upload
      const formDataToSend = new FormData();
      formDataToSend.append('first_name', formData.firstName);
      formDataToSend.append('last_name', formData.lastName);
      formDataToSend.append('phone_number', formData.phone);

      // Add avatar if selected
      if (avatarFile) {
        formDataToSend.append('avatar', avatarFile);
      }

      // Call backend API to update profile (multipart/form-data for avatar support)
      const response = await apiClient.patch('/admin/users/profile/', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const updated = response.data?.data;

      // Persist the updated user (including new avatar_url) into the auth store
      if (updated && user) {
        const { setUser } = useAuthStore.getState();
        setUser({
          ...user,
          first_name: updated.first_name ?? user.first_name,
          last_name: updated.last_name ?? user.last_name,
          phone_number: updated.phone_number ?? user.phone_number,
          avatar_url: updated.avatar_url ?? user.avatar_url,
        });
        // Update local preview to the server URL so it persists after clearing avatarFile
        if (updated.avatar_url) {
          setAvatarPreview(updated.avatar_url);
        }
      }
      setAvatarFile(null); // clear the pending file — photo is now saved

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save profile');
      console.error('Error saving profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = () => {
    return `${formData.firstName.charAt(0)}${formData.lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Profile Settings
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280' }}>
          Manage your account information
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Profile updated successfully!
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Profile Avatar Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar
              src={avatarPreview || undefined}
              sx={{
                width: 100,
                height: 100,
                bgcolor: 'primary.main',
                fontSize: '2rem',
              }}
            >
              {getInitials()}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {formData.firstName} {formData.lastName}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                {user?.role || 'Admin'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Profile Photo */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Profile Photo
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '8px',
                backgroundColor: '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {avatarPreview ? (
                <Box
                  component="img"
                  src={avatarPreview}
                  alt="Avatar preview"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                  No photo
                </Typography>
              )}
            </Box>

            <Box sx={{ flex: 1 }}>
              <input
                accept="image/*"
                type="file"
                id="avatar-input"
                onChange={handleAvatarChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="avatar-input">
                <Button
                  variant="contained"
                  component="span"
                  sx={{ mr: 1 }}
                >
                  Choose Photo
                </Button>
              </label>
              {avatarFile && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setAvatarFile(null);
                    setAvatarPreview('');
                  }}
                >
                  Remove
                </Button>
              )}
              <Typography
                variant="caption"
                display="block"
                sx={{ mt: 1, color: '#6b7280' }}
              >
                JPG, PNG, GIF up to 10MB. Square image recommended.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Personal Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Personal Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                variant="outlined"
                size="small"
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                variant="outlined"
                size="small"
                disabled
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Save Changes'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                // Reset form to original values
                setFormData({
                  firstName: user?.first_name || '',
                  lastName: user?.last_name || '',
                  email: user?.email || '',
                  phone: user?.phone_number || '',
                });
                setAvatarFile(null);
                setAvatarPreview('');
                setError(null);
                setSuccess(false);
              }}
            >
              Cancel
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Account Information */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Account Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>
                ACCOUNT TYPE
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {user?.role || 'Admin'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>
                STATUS
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, color: '#10b981' }}>
                Active
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Change Password Section */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Change Password
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {!showPasswordForm ? (
            <Button variant="contained" onClick={() => setShowPasswordForm(true)}>
              Change Password
            </Button>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Current Password"
                    name="currentPassword"
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordChange}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="New Password"
                    name="newPassword"
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordChange}
                    variant="outlined"
                    size="small"
                    helperText="Minimum 8 characters"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Confirm New Password"
                    name="confirmPassword"
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordChange}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleChangePassword}
                  disabled={passwordLoading}
                >
                  {passwordLoading ? <CircularProgress size={24} /> : 'Change Password'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    setError(null);
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}
